import {Component, computed, effect, input, model, inject} from '@angular/core';
import {ReactiveFormsModule, FormGroup} from '@angular/forms';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {Panel} from 'primeng/panel';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {NgClass, DatePipe} from '@angular/common';
import {FunctionalityFeeModuleData, ServiceFeesContainerData} from './service-fees-container.types';
import {TableModule} from 'primeng/table';
import {TranslocoPipe} from '@jsverse/transloco';
import {AppCurrencyPipe} from '@frontend/shared';
import {PanelHeaderComponent} from '../../../../../../../../shared/components/header/panel-header/panel-header.component';
import {FormControlsConfig} from '../../../step-8-1-proposal-parameter/components/abstract-form-directive';
import {CommentModalButtonComponent} from '../../../../../../../../shared/components/dialog/comment-modal-button/comment-modal-button-reactive/comment-modal-button.component';
import {SumFees} from '../step-8-2-3-rfq-preview-price-list.types';
import {AbstractFormArrayDirective} from '../../../step-8-1-proposal-parameter/components/abstract-form-array-directive';
import {toSignal} from '@angular/core/rxjs-interop';
import {startWith} from 'rxjs';
import {FormFieldNumberComponent} from '../../../../../../../../shared/components/inputs/form-field/form-field-number/form-field-number.component';
import {RfqFeeCategoryService} from '../../../../../../../../shared/services/rfq-fee-category.service';

interface FeeRowVM {
  data: FunctionalityFeeModuleData;
  officeHeadcount: number;
  eligibleHeadcount: number;
  yearlyFees: number[];
  totalAcrossScope: number;
}

@Component({
  selector: 'app-service-fees-module',
  imports: [
    ReactiveFormsModule,
    Panel,
    ProjectStepSaveButtonComponent,
    NgClass,
    PanelHeaderComponent,
    TableModule,
    TranslocoPipe,
    TranslateDirectusPipe,
    AppCurrencyPipe,
    CommentModalButtonComponent,
    FormFieldNumberComponent,
    DatePipe,
  ],
  templateUrl: './service-fees.component.html',
  providers: [TranslateDirectusPipe],
})
export class ServiceFeesComponent extends AbstractFormArrayDirective<
  ServiceFeesContainerData,
  FunctionalityFeeModuleData
> {
  protected readonly feeCategories = inject(RfqFeeCategoryService);
  private static readonly FORM_ARRAY_CONTROLS: FormControlsConfig<ServiceFeesContainerData> = {
    locale: [''],
    currencyCode: [''],
    scopeYears: [[]],
    data: [[]],
    companyEmployeesData: [[]],
  };

  protected calculate = input<boolean>(false);

  private readonly formValue!: ReturnType<typeof toSignal>;

  readonly feeRows = computed<FeeRowVM[]>(() => {
    this.formValue?.();
    const config = this.inputObject();
    if (!config?.data) return [];
    const formData = this.theForm.get('data')?.value ?? [];

    return config.data.map((feeModule, index) => {
      const formRow = formData[index];
      const formFeePerUnit = formRow?.feePerUnit ?? feeModule.feePerUnit;
      const officeHeadcount = this.getOfficeHeadcount(feeModule);
      const formEligibleHeadcount =
        formRow?.eligibleHeadcount ?? feeModule.eligibleHeadcount ?? officeHeadcount;
      const yearlyFees: number[] = config.scopeYears.map(
        () => formFeePerUnit * formEligibleHeadcount,
      );

      return {
        data: feeModule,
        officeHeadcount,
        eligibleHeadcount: formEligibleHeadcount,
        yearlyFees,
        totalAcrossScope: yearlyFees.reduce((sum, v) => sum + v, 0),
      };
    });
  });

  readonly columnTotalsPerYear = computed<number[]>(() =>
    this.inputObject().scopeYears.map((_, yearIndex) =>
      this.feeRows().reduce((sum, row) => sum + (row.yearlyFees[yearIndex] ?? 0), 0),
    ),
  );

  readonly grandTotalAllYears = computed<number>(() =>
    this.feeRows().reduce((sum, row) => sum + row.totalAcrossScope, 0),
  );

  readonly sumFees = computed<SumFees>(() => {
    const columnTotalsPerYear = this.columnTotalsPerYear();
    const grandTotalAllYears = this.grandTotalAllYears();
    return {
      columnTotalsPerYear: columnTotalsPerYear,
      grandTotalAllYears: grandTotalAllYears,
    } as SumFees;
  });

  sumFeesModel = model<SumFees>({} as SumFees);

  constructor() {
    super(ServiceFeesComponent.FORM_ARRAY_CONTROLS);
    this.formValue = toSignal(this.theForm.valueChanges.pipe(startWith(this.theForm.value)));
    effect(() => {
      this.sumFeesModel.set(this.sumFees());
    });
  }

  readonly emailEditingDisabled = input<boolean>(true);

  protected createRowFormGroup(item: FunctionalityFeeModuleData): FormGroup {
    const defaultHeadcount = this.getOfficeHeadcount(item);
    const initialEligible = item.eligibleHeadcount ?? defaultHeadcount;

    return this.fb.group({
      moduleFunction: [item.moduleFunction],
      offices: [item.offices],
      eligibleHeadcount: [initialEligible],
      feePerUnit: [item.feePerUnit],
      comment: [item.comment],
    });
  }

  private getOfficeHeadcount(item: FunctionalityFeeModuleData): number {
    const targetOfficeIds = item.offices.map((o) => o.officeLookupResponse.id);
    return this.inputObject()
      .companyEmployeesData.filter((entry) => targetOfficeIds.includes(entry.office.id))
      .reduce((sum, current) => sum + current.totalHeadcount, 0);
  }
}
